import React, { useState, useEffect, useRef } from 'react';
import { IconButton, Stack } from '@fluentui/react';
import { EditRegular, AttachRegular, DatabaseRegular, ChevronDownRegular, ChevronUpRegular, BrainCircuitRegular, BrainCircuitFilled, TranslateRegular, ArrowUploadRegular } from '@fluentui/react-icons';
import { DataSourceSelector } from '../DataSourceSelector';
import { UserPreferences, updateUserPreferences } from '../../api/dataSourceApi';
import { useDataContext } from '../../contexts/DataContext';
import { ActionsMenu } from '../ActionsMenu';
import { CreateResearchScopeWizard, ResearchScopeDocPanel, ResearchScopeSharingModal } from '../ResearchScope';
import { ResearchScopeDetail, getResearchScope } from '../../api/researchScopeApi';
import { CreateDocumentWorkspaceWizard } from '../DocumentWorkspace/CreateDocumentWorkspaceWizard';
import { DocumentDraftPanel } from '../DocumentDraft/DocumentDraftPanel';
import { DocumentWorkspace } from '../../api/documentWorkspaceApi';
import styles from './ChatControls.module.css';

interface ChatControlsProps {
  // UI Configuration
  ui?: {
    show_upload_button?: boolean;
  };
  
  // States
  modelDropdownOpen: boolean;
  selectedDataSource: string | null;
  selectedModel: string;
  
  // Options
  modelOptions: Array<{ id: string; label: string }>;
  
  // Handlers
  onNewChat: () => void;
  onFileUpload: () => void;
  onTranslateUpload?: () => void;  // Direct translate flow - skip analysis
  onModelDropdownToggle: () => void;
  onModelSelection: (modelId: string) => void;
  onDataSourceChange: (dataSourceId: string | null) => void;
  onPreferencesChange?: (preferences: UserPreferences) => void;
  onResearchScopeCreated?: (scope: ResearchScopeDetail) => void;
  onDocumentWorkspaceCreated?: (workspace: DocumentWorkspace) => void;
  onEditDocumentWorkspace?: (workspaceId: string) => void;  // Called when workspace is selected for editing
  onPinDocumentWorkspace?: (workspaceId: string) => void;
  pinnedWorkspaceId?: string | null;
  
  // Styling
  style?: React.CSSProperties;
}

export const ChatControls: React.FC<ChatControlsProps> = ({
  ui,
  modelDropdownOpen,
  selectedDataSource,
  selectedModel,
  modelOptions,
  onNewChat,
  onFileUpload,
  onTranslateUpload,
  onModelDropdownToggle,
  onModelSelection,
  onDataSourceChange,
  onPreferencesChange,
  onResearchScopeCreated,
  onDocumentWorkspaceCreated,
  onEditDocumentWorkspace,
  onPinDocumentWorkspace,
  pinnedWorkspaceId,
  style
}) => {
  const { userPreferences, isLoading, refreshUserPreferences, refreshAvailableDataSources } = useDataContext();
  const [preferences, setPreferences] = useState<UserPreferences>({
    use_dynamic_system_message: false
  });
  const modelDropdownRef = useRef<HTMLDivElement>(null);
  const uploadDropdownRef = useRef<HTMLDivElement>(null);
  
  // Upload dropdown state
  const [uploadDropdownOpen, setUploadDropdownOpen] = useState(false);
  
  // Research Scope Wizard state
  const [isResearchScopeWizardOpen, setIsResearchScopeWizardOpen] = useState(false);
  
  // Document Workspace Wizard state
  const [isDocumentWorkspaceWizardOpen, setIsDocumentWorkspaceWizardOpen] = useState(false);
  // Document Workspace Edit Panel state
  const [editingWorkspaceId, setEditingWorkspaceId] = useState<string | null>(null);
  // Research Scope Edit Panel state
  const [editingScopeId, setEditingScopeId] = useState<string | null>(null);
  // Research Scope Sharing Modal state
  const [sharingScope, setSharingScope] = useState<ResearchScopeDetail | null>(null);

  // Close model dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modelDropdownOpen && modelDropdownRef.current && !modelDropdownRef.current.contains(event.target as Node)) {
        onModelDropdownToggle();
      }
    };

    if (modelDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [modelDropdownOpen, onModelDropdownToggle]);

  // Close upload dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (uploadDropdownOpen && uploadDropdownRef.current && !uploadDropdownRef.current.contains(event.target as Node)) {
        setUploadDropdownOpen(false);
      }
    };

    if (uploadDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [uploadDropdownOpen]);

  // Sync with context data when available
  useEffect(() => {
    if (userPreferences) {
      setPreferences(userPreferences);
      onPreferencesChange?.(userPreferences);
      
      // NOTE: Removed automatic onDataSourceChange call to prevent override
      // when DataSourceSelector saves "All Sources" (null). The DataSourceSelector
      // handles its own state management and doesn't need ChatControls to override it.
    }
  }, [userPreferences, onPreferencesChange]);

  const updatePreference = async (key: keyof UserPreferences, value: boolean) => {
    // Set optimistic state immediately
    const updatedPreferences = { ...preferences, [key]: value };
    setPreferences(updatedPreferences);
    onPreferencesChange?.(updatedPreferences);
    
    try {
      const savedPreferences = await updateUserPreferences({ [key]: value });
      
      // Only update if the API response is different from our optimistic update
      if (savedPreferences[key] !== value) {
        setPreferences(savedPreferences);
        onPreferencesChange?.(savedPreferences);
      }
      
      // Refresh the context data
      await refreshUserPreferences();
    } catch (error) {
      console.error(`Failed to update ${key}:`, error);
      // Keep the optimistic state, don't revert
      // The user will see their change, and we can retry in the background
    }
  };

  return (
    <div style={style}>
      {/* Control Buttons */}
      <Stack horizontal tokens={{ childrenGap: 12 }} className={styles.controlsContainer}>
        <IconButton
          title="Start a new conversation - Clear all previous messages and context"
          onClick={onNewChat}
          className={styles.controlButton}
          styles={{
            root: { minWidth: '40px' },
            rootHovered: { 
              background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.12) 0%, rgba(118, 75, 162, 0.08) 100%)',
              borderColor: 'rgba(102, 126, 234, 0.3)',
              color: '#667eea'
            }
          }}
        >
          <EditRegular style={{ fontSize: '16px' }} />
        </IconButton>
        
        {ui?.show_upload_button === true && (
          <>
            {/* Upload Dropdown - Upload & Analyze or Translate */}
            <div ref={uploadDropdownRef} style={{ position: 'relative' }}>
              <IconButton
                title="Upload files - Click for options: Upload & Analyze or Translate Documents"
                onClick={() => setUploadDropdownOpen(!uploadDropdownOpen)}
                className={`${styles.controlButton} ${uploadDropdownOpen ? styles.controlButtonActive : ''}`}
                styles={{
                  root: { minWidth: '40px' },
                  rootHovered: { 
                    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.12) 0%, rgba(118, 75, 162, 0.08) 100%)',
                    borderColor: 'rgba(102, 126, 234, 0.3)',
                    color: '#667eea'
                  }
                }}
              >
                <AttachRegular style={{ fontSize: '16px' }} />
                <div className={styles.chevronIndicator}>
                  {uploadDropdownOpen ? <ChevronUpRegular /> : <ChevronDownRegular />}
                </div>
              </IconButton>
              
              {uploadDropdownOpen && (
                <div className={styles.dropdownMenu}>
                  <div
                    onClick={() => {
                      setUploadDropdownOpen(false);
                      onFileUpload();
                    }}
                    className={styles.dropdownItem}
                  >
                    <ArrowUploadRegular style={{ fontSize: '14px', marginRight: '8px' }} />
                    Upload & Analyze
                  </div>
                  {onTranslateUpload && (
                    <div
                      onClick={() => {
                        setUploadDropdownOpen(false);
                        onTranslateUpload();
                      }}
                      className={styles.dropdownItem}
                    >
                      <TranslateRegular style={{ fontSize: '14px', marginRight: '8px' }} />
                      Translate Documents
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Data Source Selector */}
            <DataSourceSelector
              selectedDataSource={selectedDataSource}
              onDataSourceChange={onDataSourceChange}
            />

            {/* User Preferences as Icon Buttons */}
            <IconButton
              title={preferences.use_dynamic_system_message ? "Dynamic System Messages: ON - AI uses context-aware system prompts. Click to use default system message." : "Dynamic System Messages: OFF - AI uses default system message. Click to enable context-aware prompts."}
              onClick={() => updatePreference('use_dynamic_system_message', !preferences.use_dynamic_system_message)}
              disabled={isLoading}
              className={`${styles.controlButton} ${preferences.use_dynamic_system_message ? styles.controlButtonActive : ''}`}
              styles={{
                root: { minWidth: '40px' },
                rootHovered: { 
                  background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.1) 100%)',
                  borderColor: 'rgba(102, 126, 234, 0.4)',
                  color: '#667eea'
                }
              }}
            >
              {preferences.use_dynamic_system_message ? <BrainCircuitFilled style={{ fontSize: '16px' }} /> : <BrainCircuitRegular style={{ fontSize: '16px' }} />}
              <div className={`${styles.statusBadge} ${!preferences.use_dynamic_system_message ? styles.statusBadgeOff : ''}`}>
                {preferences.use_dynamic_system_message ? '✓' : '✗'}
              </div>
            </IconButton>
            
            {/* Model Selection */}
            <div ref={modelDropdownRef} style={{ position: 'relative' }}>
              <IconButton
                title={selectedModel ? `AI Model: ${modelOptions.find(m => m.id === selectedModel)?.label || selectedModel} - Click to change model` : "Select AI Model - Choose which AI model to use for responses"}
                onClick={onModelDropdownToggle}
                className={`${styles.controlButton} ${(modelDropdownOpen || selectedModel) ? styles.controlButtonActive : ''}`}
                styles={{
                  root: { minWidth: '40px' },
                  rootHovered: { 
                    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.1) 100%)',
                    borderColor: 'rgba(102, 126, 234, 0.4)',
                    color: '#667eea'
                  }
                }}
              >
                <DatabaseRegular style={{ fontSize: '16px' }} />
                <div className={styles.chevronIndicator}>
                  {modelDropdownOpen ? <ChevronUpRegular /> : <ChevronDownRegular />}
                </div>
                {selectedModel && (
                  <div className={styles.statusBadge}>
                    ✓
                  </div>
                )}
              </IconButton>
              
              {modelDropdownOpen && modelOptions && modelOptions.length > 0 && (
                <div className={styles.dropdownMenu}>
                  {modelOptions.map(model => (
                    <div
                      key={model.id}
                      onClick={() => onModelSelection(model.id)}
                      className={`${styles.dropdownItem} ${selectedModel === model.id ? styles.dropdownItemSelected : ''}`}
                    >
                      {model.label}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Actions Menu - Research Scopes, Deep Research, etc. */}
            <ActionsMenu
              onCreateResearchScope={() => setIsResearchScopeWizardOpen(true)}
              onEditResearchScope={(scopeId) => setEditingScopeId(scopeId)}
              onCreateDocumentWorkspace={() => setIsDocumentWorkspaceWizardOpen(true)}
              onEditDocumentWorkspace={(workspaceId) => {
                // If parent provides handler, use it (allows parent to check workspace mode)
                if (onEditDocumentWorkspace) {
                  onEditDocumentWorkspace(workspaceId);
                } else {
                  // Fallback to internal panel handling
                  setEditingWorkspaceId(workspaceId);
                }
              }}
              onPinDocumentWorkspace={onPinDocumentWorkspace}
              pinnedWorkspaceId={pinnedWorkspaceId}
              disabled={isLoading}
            />
          </>
        )}
      </Stack>

      {/* Research Scope Wizard Modal */}
      <CreateResearchScopeWizard
        isOpen={isResearchScopeWizardOpen}
        onClose={() => setIsResearchScopeWizardOpen(false)}
        onCreated={(scope) => {
          onResearchScopeCreated?.(scope);
          // Refresh data sources to show new scope in dropdown
          refreshAvailableDataSources?.();
          // Optionally select the new scope as data source
          onDataSourceChange?.(`research_scope_${scope.id}`);
        }}
      />

      {/* Research Scope Edit Panel */}
      {editingScopeId && (
        <ResearchScopeDocPanel
          scopeId={editingScopeId}
          isOpen={!!editingScopeId}
          onClose={() => {
            setEditingScopeId(null);
            // Refresh data sources in case files were added
            refreshAvailableDataSources?.();
          }}
          onOpenSharing={async () => {
            // Fetch the scope data to pass to the sharing modal
            try {
              const scope = await getResearchScope(editingScopeId);
              setSharingScope(scope);
            } catch (error) {
              console.error('Failed to load scope for sharing:', error);
            }
          }}
          onUpdated={() => {
            // Refresh data sources when scope is updated
            refreshAvailableDataSources?.();
          }}
          onDeleted={() => {
            setEditingScopeId(null);
            // Clear the selected data source since it was deleted
            onDataSourceChange?.('');
            // Refresh data sources to remove deleted scope
            refreshAvailableDataSources?.();
          }}
        />
      )}

      {/* Research Scope Sharing Modal */}
      {sharingScope && (
        <ResearchScopeSharingModal
          scope={sharingScope}
          isOpen={!!sharingScope}
          onClose={() => setSharingScope(null)}
          onUpdated={(updatedScope) => {
            setSharingScope(null);
            // Refresh data sources if sharing changed
            refreshAvailableDataSources?.();
          }}
        />
      )}

      {/* Document Workspace Wizard Modal */}
      <CreateDocumentWorkspaceWizard
        isOpen={isDocumentWorkspaceWizardOpen}
        onClose={() => setIsDocumentWorkspaceWizardOpen(false)}
        onCreated={(workspace) => {
          onDocumentWorkspaceCreated?.(workspace);
        }}
      />

      {/* Document Workspace Edit Panel - only show if external handler not provided */}
      {!onEditDocumentWorkspace && editingWorkspaceId && (
        <DocumentDraftPanel
          draft={null}
          workspaceId={editingWorkspaceId}
          conversationId=""
          isRtl={false}
          onClose={() => setEditingWorkspaceId(null)}
        />
      )}
    </div>
  );
};